import React, { FC, useRef, useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { gsap } from 'gsap/all'
import clsx from 'clsx'
import Subtitle from 'src/components/ui/Subtitle'
import H2 from 'src/components/ui/title/H2'
import StatItem from 'src/components/ui/StatItem'
import { useBasicTeaserAnimations } from 'src/components/sections/teaser/basic-teaser/animations'
import { GLOBAL_PREVIEW_MODE } from 'src/helpers/preview'
import {
  IBasicTeaserSectionProps,
  IStatisticItem,
} from './types'

export * from './types'

const DEFAULT_STATISTICS: IStatisticItem[] = [
  { value: '20', suffix: '+', label: 'Years of Experience' },
  { value: '100', suffix: '+', label: 'Client Projects' },
  { value: '100', suffix: '%', label: 'Satisfaction Rate' },
]

const BasicTeaserSection: FC<IBasicTeaserSectionProps> = ({
  translationContent: {
    firstPartOfTitle,
    secondPartOfTitle,
    subtitle,
    image,
    sectionName,
    statisticsSubtitle,
    statistics,
  },
  customTag = 'section',
  hideImageOnMobile = false,
  className = '',
  children,
  mobileFooterChildren = null,
  isPreview,
  previewImageUrl,
  layoutVariant = 'split',
  showStatistics = false,
  hideDefaultHeader = false,
  isFullWidthText = false,
  imageLink,
  imageOverlayLabel,
  imageChildren,
  hideImage = false,
  imageFit = 'natural',
  splitRatio = '1/2',
  imageMarginTop,
  imageAlignTop = false,
  paddingClassName,
  headerChildren,
}) => {
  const subtitleRef = useRef<HTMLDivElement>(null)
  const largeTitleRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const footerButtonRef = useRef<HTMLDivElement>(null)
  const imgRef = useRef<HTMLDivElement>(null)
  const magneticTargetRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const relativePhotoContainerRef = useRef<HTMLDivElement>(null)
  const cursorRef = useRef<HTMLDivElement>(null)
  const statisticsSubtitleRef = useRef<HTMLHeadingElement>(null)
  const [isMounted, setIsMounted] = useState(false)

  useEffect(() => {
    setIsMounted(true)
    if (cursorRef.current) {
      gsap.set(cursorRef.current, { xPercent: -50, yPercent: -50 })
    }
  }, [])

  const handleMouseMove = (e: React.MouseEvent) => {
    if (typeof window !== 'undefined' && (window.innerWidth < 768 || !window.matchMedia('(hover: hover)').matches)) return
    if (cursorRef.current) {
      gsap.to(cursorRef.current, {
        x: e.clientX,
        y: e.clientY,
        duration: 0.15,
        ease: 'power2.out',
        overwrite: 'auto',
      })
    }

    if (magneticTargetRef.current && imgRef.current) {
      const rect = (imgRef.current as HTMLElement).getBoundingClientRect()
      const centerX = rect.left + rect.width / 2
      const centerY = rect.top + rect.height / 2
      const distanceX = e.clientX - centerX
      const distanceY = e.clientY - centerY

      gsap.to(magneticTargetRef.current, {
        x: distanceX * 0.04,
        y: distanceY * 0.04,
        duration: 0.9,
        ease: 'power3.out',
        overwrite: 'auto',
      })
    }
  }

  const handleMouseEnter = (e: React.MouseEvent) => {
    if (typeof window !== 'undefined' && (window.innerWidth < 768 || !window.matchMedia('(hover: hover)').matches)) return
    if (cursorRef.current) {
      gsap.set(cursorRef.current, { x: e.clientX, y: e.clientY })
      gsap.to(cursorRef.current, {
        scale: 1,
        opacity: 1,
        duration: 0.3,
        ease: 'power2.out',
      })
    }
  }

  const handleMouseLeave = () => {
    if (typeof window !== 'undefined' && (window.innerWidth < 768 || !window.matchMedia('(hover: hover)').matches)) return
    if (cursorRef.current) {
      gsap.to(cursorRef.current, {
        scale: 0,
        opacity: 0,
        duration: 0.3,
        ease: 'power2.in',
      })
    }

    if (magneticTargetRef.current) {
      gsap.to(magneticTargetRef.current, {
        x: 0,
        y: 0,
        duration: 0.9,
        ease: 'power3.out',
        overwrite: 'auto',
      })
    }
  }

  useBasicTeaserAnimations(
    subtitleRef as React.MutableRefObject<HTMLDivElement>,
    largeTitleRef as React.MutableRefObject<HTMLDivElement>,
    contentRef as React.MutableRefObject<HTMLDivElement>,
    imgRef as React.MutableRefObject<HTMLDivElement>,
    footerButtonRef as React.MutableRefObject<HTMLDivElement>,
    statisticsSubtitleRef
  )

  const ContainerTag = `${customTag}` as keyof JSX.IntrinsicElements
  const isPreviewMode = isPreview !== undefined ? isPreview : GLOBAL_PREVIEW_MODE
  const teaserImage = isPreviewMode
    ? (previewImageUrl || '/images/placeholders/home/about-us.webp')
    : image?.url

  const isSplit = layoutVariant === 'split' && !hideImage
  const hasBottomImage = !hideImage && !hideImageOnMobile

  const marginTopClass = typeof imageMarginTop === 'string'
    ? imageMarginTop
    : imageMarginTop === true
      ? 'md:pt-12'
      : ''

  const flexAlignClass = imageAlignTop
    ? 'items-start justify-center'
    : (imageFit === 'cover' ? 'items-stretch' : 'items-center justify-center')

  const pyClass = paddingClassName || 'py-huge sm:py-huge md:py-massive lg:py-gigantic xl:py-colossal'

  const textColumnWidthClass = (isFullWidthText || hideImage)
    ? 'md:w-full'
    : isSplit
      ? headerChildren
        ? 'md:w-7/12 xl:w-1/2 xl:flex-1'
        : splitRatio === '1/2'
          ? 'md:w-7/12 xl:w-1/2'
          : 'md:w-7/12'
      : 'md:w-full'

  const imageColumnWidthClass = isSplit
    ? headerChildren
      ? 'md:w-5/12 xl:w-1/2 xl:flex-1'
      : splitRatio === '1/2'
        ? 'md:w-5/12 xl:w-1/2'
        : 'md:w-5/12'
    : 'w-full'

  return (
    <ContainerTag
      data-name={sectionName}
      id={sectionName}
      className={clsx(
        'relative bg-transparent',
        isSplit ? 'min-h-screen md:min-h-screen flex items-stretch' : 'flex flex-col',
        className
      )}
    >
      <div
        ref={containerRef}
        className={clsx(
          'container flex flex-col justify-center items-stretch w-full min-h-full',
          headerChildren
            ? 'gap-base md:gap-semi-large xl:gap-extra-large'
            : hasBottomImage ? 'gap-large md:gap-base' : 'gap-small md:gap-base',
          pyClass
        )}
      >
        {headerChildren && (
          <div className="w-full">
            {headerChildren}
          </div>
        )}

        <div
          className={clsx(
            'flex flex-col items-start w-full',
            isSplit ? 'md:flex-row' : 'flex-col',
            hasBottomImage
              ? headerChildren ? 'gap-large md:gap-base' : 'gap-large md:gap-none'
              : headerChildren ? 'gap-small md:gap-base' : 'gap-small md:gap-none'
          )}
        >
          {/* TEXT CONTENT COLUMN */}
          <div
            className={clsx(
              'w-full flex flex-col',
              textColumnWidthClass,
              isSplit && !hideImage
                ? headerChildren
                  ? 'justify-start'
                  : 'md:pr-colossal justify-center'
                : 'mb-none'
            )}
          >
            <div className="flex flex-col">
              {!hideDefaultHeader && (
                <div className="mb-extra-small md:mb-small">
                  <Subtitle ref={subtitleRef} className="opacity-0">
                    {subtitle}
                  </Subtitle>
                  <H2
                    className=""
                    ref={largeTitleRef}
                    firstPart={firstPartOfTitle}
                    secondPart={secondPartOfTitle}
                  />
                </div>
              )}

              <div
                ref={contentRef}
                className="opacity-0 z-10 flex flex-col items-stretch md:items-start [&_a]:w-full md:[&_a]:w-auto [&_button]:w-full md:[&_button]:w-auto"
              >
                {children}
              </div>
            </div>

            {!!mobileFooterChildren && (
              <div
                ref={footerButtonRef}
                className="opacity-0 md:hidden mt-small overflow-hidden flex flex-col items-stretch"
              >
                {mobileFooterChildren}
              </div>
            )}
          </div>

          {/* IMAGE COLUMN */}
          {!hideImage && !hideImageOnMobile && (
            <div
              ref={relativePhotoContainerRef}
              className={clsx(
                'w-full relative flex',
                imageColumnWidthClass,
                headerChildren ? 'items-start justify-center' : flexAlignClass,
                !headerChildren && marginTopClass
              )}
            >
              {isMounted && imageOverlayLabel && typeof document !== 'undefined'
                ? createPortal(
                  <div
                    ref={cursorRef}
                    className="pointer-events-none fixed left-none top-none z-50 flex h-24 w-24 items-center justify-center rounded-full bg-primary text-white opacity-0"
                    style={{ transform: 'scale(0)' }}
                  >
                    <span className="font-palanquin text-sm font-bold uppercase tracking-widest text-center px-pico">
                      {imageOverlayLabel}
                    </span>
                  </div>,
                  document.body
                )
                : null}

              <div
                ref={imgRef}
                className={clsx(
                  'opacity-0 w-full relative group',
                  imageFit === 'cover' ? 'rounded-container-extra-large overflow-hidden' : 'overflow-visible',
                  imageFit === 'cover'
                    ? clsx('h-full aspect-2/3', isSplit ? 'md:aspect-auto' : 'md:aspect-video')
                    : 'h-auto',
                  imageLink && 'md:cursor-none'
                )}
                onMouseMove={handleMouseMove}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              >
                <div ref={magneticTargetRef} className="w-full h-full will-change-transform">
                  {imageLink ? (
                    <a
                      href={imageLink}
                      target="_blank"
                      rel="noreferrer"
                      className={clsx('block w-full', imageFit === 'cover' ? 'h-full' : 'h-auto')}
                    >
                      <img
                        src={teaserImage}
                        alt={image?.alt || ''}
                        className={clsx(
                          'w-full transform transition-transform duration-700 ease-out md:group-hover:scale-110',
                          imageFit === 'cover' ? 'h-full object-cover' : 'h-auto block object-contain'
                        )}
                      />
                      {imageChildren && (
                        <div className="absolute inset-0 z-10 flex flex-col justify-end p-extra-small pointer-events-none">
                          {imageChildren}
                        </div>
                      )}
                    </a>
                  ) : (
                    <>
                      <img
                        src={teaserImage}
                        alt={image?.alt || ''}
                        className={clsx(
                          'w-full',
                          imageFit === 'cover' ? 'h-full object-cover' : 'h-auto block object-contain'
                        )}
                      />
                      {imageChildren && (
                        <div className="absolute inset-0 z-10 flex flex-col justify-end p-extra-small pointer-events-none">
                          {imageChildren}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Statistics for Split Layout - Full Width Under Text and Image */}
        {isSplit && showStatistics && (
          <div className={clsx('w-full mt-colossal flex flex-col gap-large xl:gap-extra-large')}>
            <Subtitle
              ref={statisticsSubtitleRef}
              className={clsx('mb-0 opacity-0')}
            >
              {statisticsSubtitle || 'by the numbers'}
            </Subtitle>
            <div
              className={clsx(
                'w-full grid grid-cols-2 sm:grid-cols-3 gap-x-base sm:gap-x-semi-large gap-y-huge',
                'md:flex md:flex-row md:justify-between md:items-start md:gap-base'
              )}
            >
              {(statistics && statistics.length > 0 ? statistics : DEFAULT_STATISTICS).map((stat, idx) => (
                <StatItem
                  key={idx}
                  value={stat.value}
                  suffix={stat.suffix}
                  label={stat.label}
                  className="w-full md:w-fit"
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </ContainerTag>
  )
}

export default BasicTeaserSection

